<div>
  <Navbar />
  <Sidebar />

  <div class="page-wrapper">
    <div class="hero-section w-100">
      <div class="border border-secondary rounded-4 p-2">
        <div class="mb-4">
          <p class="platform-page-kicker mb-2">Platform Management</p>
          <h2 class="gradient-text fw-bold mb-1">Platform Configuration &amp; Launch</h2>
          <p class="text-muted mb-0">Configure connected platforms, access, readiness, and launch testing</p>
        </div>

        <div class="row text-center mb-3">
          <div class="col-md-6 col-xl-3 mb-3">
            <div class="card rounded-4 shadow-sm platform-kpi-card h-100">
              <div class="card-body">
                <p class="card-text display-6 mb-1">{{ connected_platforms_count }}</p>
                <h5 class="card-title">Connected Platforms</h5>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-3 mb-3">
            <div class="card rounded-4 shadow-sm platform-kpi-card h-100">
              <div class="card-body">
                <p class="card-text display-6 mb-1">{{ active_configurations_count }}</p>
                <h5 class="card-title">Active Configurations</h5>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-3 mb-3">
            <div class="card rounded-4 shadow-sm platform-kpi-card h-100">
              <div class="card-body">
                <p class="card-text display-6 mb-1">{{ ready_for_launch_count }}</p>
                <h5 class="card-title">Ready for DevOps Handover</h5>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-3 mb-3">
            <div class="card rounded-4 shadow-sm platform-kpi-card h-100">
              <div class="card-body">
                <p class="card-text display-6 mb-1">{{ access_assignments_count }}</p>
                <h5 class="card-title">Access Assignments</h5>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2 mb-3">
          <button
            class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
            data-bs-toggle="modal"
            data-bs-target="#addPlatformModal"
            @click="prepare_add_platform_modal"
          >
            <i class="fas fa-plus plus-icon me-1"></i> Add Platform
          </button>

          <button
            class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
            @click="$router.push({ name: 'AddPlatformConfig' })"
          >
            <i class="fas fa-layer-group me-1"></i> Add Config
          </button>

          <button
            class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
            :disabled="readiness_loading || !selected_configuration"
            @click="run_readiness_check()"
          >
            <span v-if="readiness_loading" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fas fa-stethoscope me-1"></i>
            Run Readiness Check
          </button>
        </div>

        <ul class="nav nav-tabs nav-justified mb-4 platform-tabs">
          <li class="nav-item">
            <button class="nav-link" :class="{ active: active_tab === 'Platforms' }" type="button" @click="set_active_tab('Platforms')">
              Platforms
            </button>
          </li>
          <li class="nav-item">
            <button class="nav-link" :class="{ active: active_tab === 'Configurations' }" type="button" @click="set_active_tab('Configurations')">
              Configurations
            </button>
          </li>
          <li class="nav-item">
            <button class="nav-link" :class="{ active: active_tab === 'Access' }" type="button" @click="set_active_tab('Access')">
              Access
            </button>
          </li>
          <li class="nav-item">
            <button class="nav-link" :class="{ active: active_tab === 'Launch Testing' }" type="button" @click="set_active_tab('Launch Testing')">
              Launch Testing
            </button>
          </li>
        </ul>

        <div v-if="active_tab === 'Platforms'" class="card table-card rounded-4 shadow-sm mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 class="mb-1">Connected Platforms</h5>
                <p class="text-muted small mb-0">This list uses live platform data from the database.</p>
              </div>
            </div>

            <div v-if="platforms_loaded && !displayed_platforms.length" class="text-center py-4 text-muted">
              No platforms found.
            </div>

            <div v-else class="table-responsive content-table">
              <table class="table table-striped text-xs platform-management-table mb-0">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Platform</th>
                    <th>App Code</th>
                    <th>Environment</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody class="text-start text-xs">
                  <tr v-for="(platform, index) in displayed_platforms" :key="platform.id || index">
                    <td>{{ index + 1 }}</td>
                    <td class="fw-semibold">{{ platform.name }}</td>
                    <td>{{ platform.app_code || 'Not configured' }}</td>
                    <td>{{ platform.environment || 'Not configured' }}</td>
                    <td>
                      <span class="badge rounded-pill" :class="get_status_badge_class(platform.status)">
                        {{ format_status_label(platform.status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="active_tab === 'Configurations'" class="platform-configurations-stack">
          <div class="row g-4 align-items-start">
            <div class="col-lg-8">
              <div class="card table-card rounded-4 shadow-sm h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <h5 class="mb-1">Platform Configurations</h5>
                      <p class="text-muted small mb-0">Select a row to inspect readiness and deployment checklist details.</p>
                    </div>
                    <span class="badge bg-light text-dark border" v-if="!configurations_loaded">Sample data</span>
                  </div>

                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="text-muted mt-2 mb-0">Loading platform configurations...</p>
                  </div>

                  <div v-else class="table-responsive content-table">
                    <table class="table table-striped text-xs platform-management-table mb-0">
                      <thead>
                        <tr>
                          <th>Platform</th>
                          <th>App Code</th>
                          <th>Environment</th>
                          <th>Base URL</th>
                          <th>Launch URL</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody class="text-start text-xs">
                        <tr
                          v-for="configuration in displayed_configurations"
                          :key="configuration.id"
                          class="platform-config-row"
                          :class="{ selected: selected_configuration_id === configuration.id }"
                          @click="set_selected_configuration(configuration)"
                        >
                          <td class="fw-semibold">{{ configuration.platform_name }}</td>
                          <td>{{ configuration.app_code }}</td>
                          <td>{{ configuration.environment }}</td>
                          <td class="platform-url-cell">{{ configuration.base_url }}</td>
                          <td class="platform-url-cell">{{ configuration.launch_url }}</td>
                          <td>
                            <span class="badge rounded-pill" :class="get_status_badge_class(configuration.status)">
                              {{ configuration.status }}
                            </span>
                          </td>
                          <td>
                            <div class="d-flex flex-wrap gap-2">
                              <button
                                class="btn btn-sm upload-button text-nowrap"
                                data-bs-toggle="modal"
                                data-bs-target="#addPlatformConfigModal"
                                @click.stop="open_edit_configuration(configuration)"
                              >
                                Edit
                              </button>
                              <button class="btn btn-sm btn-outline-dark text-nowrap" @click.stop="view_configuration(configuration)">
                                View
                              </button>
                              <button class="btn btn-sm btn-outline-dark text-nowrap" @click.stop="open_view_checklist_modal(configuration)" data-bs-toggle="modal" data-bs-target="#viewDeploymentChecklistModal">
                                View Handover
                              </button>
                              <button class="btn btn-sm btn-outline-danger text-nowrap" @click.stop="test_launch(configuration)" :disabled="launch_loading_id === configuration.id">
                                <span v-if="launch_loading_id === configuration.id" class="spinner-border spinner-border-sm me-1"></span>
                                Test Launch
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="card rounded-4 shadow-sm platform-side-card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5 class="mb-1">Readiness Panel</h5>
                      <p class="text-muted small mb-0">Readiness checks run against the selected configuration.</p>
                    </div>
                    <span class="platform-status-dot" :class="{ ready: readiness_result.reachable_status === 'Reachable' }"></span>
                  </div>

                  <div class="row g-3">
                    <div class="col-12">
                      <div class="platform-side-meta h-100">
                        <div class="small text-muted">Selected Platform</div>
                        <div class="fw-semibold">{{ readiness_panel_title }}</div>
                      </div>
                    </div>

                    <div class="col-12">
                      <div class="platform-side-meta h-100">
                        <div class="small text-muted">Environment</div>
                        <div class="fw-semibold">{{ readiness_result.environment }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="platform-readiness-list mt-3">
                    <div class="platform-readiness-item">
                      <span>Health Endpoint status</span>
                      <span class="badge rounded-pill" :class="get_status_badge_class(readiness_result.health_endpoint_status)">{{ readiness_result.health_endpoint_status }}</span>
                    </div>
                    <div class="platform-readiness-item">
                      <span>Upsert Token Endpoint status</span>
                      <span class="badge rounded-pill" :class="get_status_badge_class(readiness_result.upsert_token_endpoint_status)">{{ readiness_result.upsert_token_endpoint_status }}</span>
                    </div>
                    <div class="platform-readiness-item">
                      <span>SSO Assert status</span>
                      <span class="badge rounded-pill" :class="get_status_badge_class(readiness_result.sso_assert_status)">{{ readiness_result.sso_assert_status }}</span>
                    </div>
                    <div class="platform-readiness-item">
                      <span>Reachable status</span>
                      <span class="badge rounded-pill" :class="get_status_badge_class(readiness_result.reachable_status)">{{ readiness_result.reachable_status }}</span>
                    </div>
                  </div>

                  <div v-if="readiness_result.last_checked_at" class="small text-muted mt-3">
                    Last checked: {{ readiness_result.last_checked_at }}
                  </div>

                  <button class="btn upload-button text-white w-100 mt-3" :disabled="readiness_loading || !selected_configuration" @click="run_readiness_check()">
                    <span v-if="readiness_loading" class="spinner-border spinner-border-sm me-2"></span>
                    Run Readiness Check
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-4">
            <div class="col-12">
              <div class="card rounded-4 shadow-sm platform-side-card">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <h5 class="mb-1">DevOps Handover</h5>
                      <p class="text-muted small mb-0">Ready for DevOps Handover means AIC One has generated the required values. Ready for Client Use comes after readiness and launch checks pass.</p>
                    </div>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <span class="badge rounded-pill bg-info-subtle text-info-emphasis">
                        {{ deployment_handover ? handover_status : 'Handover not viewed' }}
                      </span>
                      <button
                        class="btn btn-sm btn-outline-dark"
                        @click="fetch_deployment_handover()"
                        :disabled="!selected_configuration"
                      >
                        View DevOps Handover
                      </button>
                    </div>
                  </div>

                  <div v-if="deployment_handover" class="border rounded-4 p-3 bg-light-subtle mb-3 text-start">
                    <div class="row g-3">
                      <div class="col-md-3">
                        <div class="small text-muted">Platform</div>
                        <div class="fw-semibold">{{ handover_platform_name }}</div>
                      </div>
                      <div class="col-md-3">
                        <div class="small text-muted">Environment</div>
                        <div class="fw-semibold">{{ handover_environment }}</div>
                      </div>
                      <div class="col-md-3">
                        <div class="small text-muted">App Code</div>
                        <div class="fw-semibold">{{ handover_app_code }}</div>
                      </div>
                      <div class="col-md-3">
                        <div class="small text-muted">Handover Status</div>
                        <div class="fw-semibold">{{ handover_status }}</div>
                      </div>
                    </div>

                    <div class="mt-3">
                      <div class="fw-semibold mb-2">Required platform routes</div>
                      <div v-if="handover_required_routes.length" class="platform-readiness-list">
                        <div class="platform-readiness-item" v-for="(route, index) in handover_required_routes" :key="route.label || route.name || index">
                          <span>{{ route.label || route.name || 'Route' }}</span>
                          <span class="text-break">{{ route.value || route.url || route }}</span>
                        </div>
                      </div>
                      <p v-else class="small text-muted mb-0">No route requirements returned for this handover.</p>
                    </div>

                    <div class="mt-3">
                      <div class="fw-semibold mb-2">Notes for DevOps</div>
                      <ul class="small text-muted mb-0 ps-3">
                        <li v-for="note in handover_notes" :key="note">{{ note }}</li>
                      </ul>
                    </div>
                  </div>

                  <div v-if="deployment_handover" class="alert alert-warning small text-start">
                    Add these values to the one client/environment .env file. Deploy using the shared YAML/docker-compose file. Run readiness checks after deployment.
                  </div>

                  <div class="platform-checklist-list" v-if="deployment_handover">
                    <div class="platform-checklist-item" v-for="entry in displayed_deployment_env_values" :key="entry.key">
                      <div class="platform-checklist-copy-grid">
                        <div>
                          <div class="small text-muted">{{ entry.key }}</div>
                          <div class="fw-semibold platform-secret-value">{{ entry.value }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="platform-checklist-list">
                    <div class="platform-checklist-item" v-for="entry in checklist_entries" :key="entry.key">
                      <div class="platform-checklist-copy-grid">
                        <div>
                          <div class="small text-muted">{{ entry.label }}</div>
                          <div class="fw-semibold platform-secret-value">{{ get_display_secret(entry.value) }}</div>
                        </div>
                        <div class="platform-checklist-copy-action">
                          <button class="btn btn-sm btn-outline-dark" @click="copy_deployment_value(entry)">
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="platform-checklist-actions d-flex gap-3 flex-wrap justify-content-center mt-4">
                    <button class="btn btn-outline-dark d-inline-flex align-items-center justify-content-center px-4 w-auto" :disabled="!selected_configuration" @click="copy_deployment_checklist()">
                      Copy Env Values
                    </button>
                    <button class="btn btn-outline-danger d-inline-flex align-items-center justify-content-center px-4 w-auto" :disabled="!deployment_handover" @click="reveal_deployment_values()">
                      Reveal Deployment Values
                    </button>
                    <button
                      class="btn upload-button text-white d-inline-flex align-items-center justify-content-center px-4 w-auto"
                      :disabled="!selected_configuration || deployment_handover_loading"
                      @click="fetch_deployment_handover()"
                    >
                      <span v-if="deployment_handover_loading">Loading...</span>
                      <span v-else>View DevOps Handover</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="active_tab === 'Access'" class="card table-card rounded-4 shadow-sm mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 class="mb-1">Platform Access Assignments</h5>
                <p class="text-muted small mb-0">Assign teams, roles, or users to platform launch and readiness workflows.</p>
              </div>
              <div class="d-flex gap-2 align-items-center">
                <span class="badge bg-light text-dark border" v-if="!access_assignments_loaded">Sample data</span>
                <button
                  class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
                  data-bs-toggle="modal"
                  data-bs-target="#assignPlatformAccessModal"
                  @click="prepare_access_modal"
                >
                  <i class="fas fa-user-plus me-1"></i> Assign Platform Access
                </button>
              </div>
            </div>

            <div class="table-responsive content-table">
              <table class="table table-striped text-xs platform-management-table mb-0">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Platform</th>
                    <th>Principal</th>
                    <th>Access Level</th>
                    <th>Environment</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody class="text-start text-xs">
                  <tr v-for="(assignment, index) in displayed_access_assignments" :key="assignment.id || index">
                    <td>{{ index + 1 }}</td>
                    <td class="fw-semibold">{{ assignment.platform_name }}</td>
                    <td>{{ assignment.principal_name }}</td>
                    <td>{{ assignment.access_level }}</td>
                    <td>{{ assignment.environment }}</td>
                    <td>
                      <span class="badge rounded-pill" :class="get_status_badge_class(assignment.status)">
                        {{ assignment.status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="active_tab === 'Launch Testing'" class="card table-card rounded-4 shadow-sm mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 class="mb-1">Launch Testing</h5>
                <p class="text-muted small mb-0">Run controlled launch tests on configured platforms and monitor the latest outcome.</p>
              </div>
              <span class="badge bg-light text-dark border" v-if="!launch_tests_loaded">Sample data</span>
            </div>

            <div class="table-responsive content-table">
              <table class="table table-striped text-xs platform-management-table mb-0">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Platform</th>
                    <th>App Code</th>
                    <th>Environment</th>
                    <th>Last Tested</th>
                    <th>Result</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody class="text-start text-xs">
                  <tr v-for="(test_row, index) in displayed_launch_tests" :key="test_row.id || index">
                    <td>{{ index + 1 }}</td>
                    <td class="fw-semibold">{{ test_row.platform_name }}</td>
                    <td>{{ test_row.app_code }}</td>
                    <td>{{ test_row.environment }}</td>
                    <td>{{ test_row.last_tested_at }}</td>
                    <td>
                      <span class="badge rounded-pill" :class="get_status_badge_class(test_row.result)">
                        {{ test_row.result }}
                      </span>
                    </td>
                    <td>
                      <button
                        class="btn btn-sm btn-outline-danger text-nowrap"
                        @click="test_launch(displayed_configurations.find((item) => item.id === test_row.id) || selected_configuration)"
                        :disabled="launch_loading_id === test_row.id"
                      >
                        <span v-if="launch_loading_id === test_row.id" class="spinner-border spinner-border-sm me-1"></span>
                        Test Launch
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal fade" id="addPlatformModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title gradient-text">Add Platform</h5>
                <button ref="close_add_platform_modal_btn" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <form @submit.prevent="submit_platform_form">
                  <div class="mb-3 text-start">
                    <label class="ms-2">Platform Name</label>
                    <input v-model="platform_form.name" type="text" class="form-control" placeholder="e.g. Proba Analytics" required>
                  </div>
                  <div class="mb-3 text-start">
                    <label class="ms-2">App Code</label>
                    <input v-model="platform_form.app_code" type="text" class="form-control" placeholder="e.g. PROBA_A" required>
                  </div>
                  <div class="mb-3 text-start">
                    <label class="ms-2">Description</label>
                    <textarea v-model="platform_form.description" class="form-control" rows="3" placeholder="Platform description"></textarea>
                  </div>
                  <div class="mb-3 text-start">
                    <label class="ms-2">Status</label>
                    <select v-model="platform_form.status" class="form-control">
                      <option>Connected</option>
                      <option>Pending</option>
                      <option>Disabled</option>
                    </select>
                  </div>
                  <div class="modal-footer px-0 pb-0">
                    <button type="submit" class="btn upload-button text-white">Save Platform</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div class="modal fade" id="addPlatformConfigModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <div>
                  <h5 class="modal-title gradient-text">Edit platform config</h5>
                  <p class="text-muted small mb-0">Update an existing platform configuration for secure launch, readiness checks, DevOps handover, and SSO access.</p>
                </div>
                <button ref="close_add_platform_config_modal_btn" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <div class="d-flex flex-wrap gap-2 mb-4">
                  <button
                    type="button"
                    class="btn btn-sm rounded-pill px-3"
                    :class="platform_config_step === 1 ? 'upload-button text-white' : 'btn-outline-dark'"
                    @click="go_to_platform_config_step(1)"
                  >
                    1. Identity
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm rounded-pill px-3"
                    :class="platform_config_step === 2 ? 'upload-button text-white' : 'btn-outline-dark'"
                    @click="go_to_platform_config_step(2)"
                  >
                    2. URLs
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm rounded-pill px-3"
                    :class="platform_config_step === 3 ? 'upload-button text-white' : 'btn-outline-dark'"
                    @click="go_to_platform_config_step(3)"
                  >
                    3. Security
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm rounded-pill px-3"
                    :class="platform_config_step === 4 ? 'upload-button text-white' : 'btn-outline-dark'"
                    @click="go_to_platform_config_step(4)"
                  >
                    4. Advanced
                  </button>
                </div>

                <form @submit.prevent="submit_platform_config_form">
                  <div v-if="platform_config_step === 1" class="border rounded-4 p-3 bg-light-subtle">
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Platform</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <select v-model="platform_config_form.platform" class="form-control" required @change="handle_platform_config_platform_change">
                        <option value="" disabled>Select platform</option>
                        <option v-for="platform_option in platform_options" :key="platform_option.id" :value="platform_option.id">
                          {{ platform_option.label }}
                        </option>
                      </select>
                      <p class="small text-muted mb-0 mt-2 px-2">Choose the platform you are connecting to AIC One.</p>
                    </div>
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>App Code</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <input v-model="platform_config_form.app_code" type="text" class="form-control" placeholder="e.g. proba_analytics or em_guidance" required>
                      <p class="small text-muted mb-0 mt-2 px-2">This is the short identifier AIC One uses for routing, launch, and DevOps handover.</p>
                    </div>
                    <div class="mb-0 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Environment</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <select v-model="platform_config_form.environment" class="form-control">
                        <option value="development">development</option>
                        <option value="staging">staging</option>
                        <option value="uat">uat</option>
                        <option value="demo">demo</option>
                        <option value="production">production</option>
                      </select>
                      <p class="small text-muted mb-0 mt-2 px-2">Choose where this platform config will be used. Avoid production until testing is complete.</p>
                    </div>
                  </div>

                  <div v-if="platform_config_step === 2" class="border rounded-4 p-3 bg-light-subtle">
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Base URL</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <input v-model="platform_config_form.base_url" type="url" class="form-control" placeholder="https://platform-domain/api" required>
                      <p class="small text-muted mb-0 mt-2 px-2">The main API base URL AIC One will use to call this platform.</p>
                    </div>
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Launch URL</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <input v-model="platform_config_form.launch_url" type="url" class="form-control" placeholder="https://platform-domain/sso/assert" required>
                      <p class="small text-muted mb-0 mt-2 px-2">The SSO assert URL where AIC One redirects users when they launch this platform.</p>
                    </div>
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Callback URL</span>
                        <span class="badge text-bg-secondary">optional</span>
                      </label>
                      <input v-model="platform_config_form.callback_url" type="url" class="form-control" placeholder="https://aic-one-domain/callback">
                      <p class="small text-muted mb-0 mt-2 px-2">Optional URL used if the platform needs to send users or responses back to AIC One.</p>
                    </div>
                    <div class="border rounded-4 p-3 bg-white">
                      <p class="fw-semibold mb-1">How launch works</p>
                      <p class="small text-muted mb-0">AIC One syncs the user, signs an SSO assertion, and redirects the browser to the platform Launch URL.</p>
                    </div>
                  </div>

                  <div v-if="platform_config_step === 3" class="border rounded-4 p-3 bg-light-subtle">
                    <div class="alert alert-info small mb-3">
                      Sensitive deployment values are generated securely by AIC One when this configuration is saved. They are not entered on this page.
                    </div>
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Signer Salt</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <input v-model="platform_config_form.signer_salt" type="text" class="form-control" placeholder="e.g. em_guidance-aic-one">
                      <p class="small text-muted mb-0 mt-2 px-2">Used to verify signed launch assertions between AIC One and the platform.</p>
                    </div>
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Max Allowed Skew</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <input v-model="platform_config_form.max_allowed_skew" type="number" class="form-control" min="0" step="1">
                      <p class="small text-muted mb-0 mt-2 px-2">The number of seconds allowed before a signed request is treated as expired. Default is 300 seconds.</p>
                    </div>
                    <div class="mb-0 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>System Code</span>
                        <span class="badge text-bg-danger">required</span>
                      </label>
                      <input v-model="platform_config_form.system" type="text" class="form-control" placeholder="e.g. em_guidance">
                      <p class="small text-muted mb-0 mt-2 px-2">The system identifier sent to the connected platform during launch and DevOps handover.</p>
                    </div>
                  </div>

                  <div v-if="platform_config_step === 4" class="border rounded-4 p-3 bg-light-subtle">
                    <div class="mb-3 text-start">
                      <label class="ms-2 d-flex align-items-center gap-2">
                        <span>Routing Rules</span>
                        <span class="badge text-bg-secondary">optional</span>
                      </label>
                      <textarea
                        v-model="platform_config_form.routing_rules_text"
                        class="form-control"
                        rows="5"
                        placeholder="[]"
                      ></textarea>
                      <p class="small text-muted mb-0 mt-2 px-2">Optional advanced routing rules. Leave as [] if no custom routing is required.</p>
                    </div>
                    <div class="mb-0 text-start">
                      <label class="ms-2">Status</label>
                      <select v-model="platform_config_form.status" class="form-control">
                        <option>Draft</option>
                        <option>Needs Review</option>
                        <option>Ready</option>
                      </select>
                      <p class="small text-muted mb-0 mt-2 px-2">Draft means the config is saved but should still be tested before being considered ready.</p>
                    </div>
                  </div>

                  <div class="modal-footer px-0 pb-0 d-flex justify-content-between">
                    <button
                      v-if="platform_config_step > 1"
                      type="button"
                      class="btn btn-outline-dark"
                      @click="previous_platform_config_step()"
                    >
                      Back
                    </button>
                    <span v-else></span>
                    <button
                      v-if="platform_config_step < 4"
                      type="button"
                      class="btn upload-button text-white"
                      @click="next_platform_config_step()"
                    >
                      Next
                    </button>
                    <button v-else type="submit" class="btn upload-button text-white">{{ editing_configuration_id ? 'Save Config' : 'Save Config' }}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div class="modal fade" id="assignPlatformAccessModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title gradient-text">Assign Platform Access</h5>
                <button ref="close_assign_platform_access_modal_btn" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <form @submit.prevent="submit_platform_access_form">
                  <div class="mb-3 text-start">
                    <label class="ms-2">Platform</label>
                    <select v-model="access_form.platform" class="form-control" required>
                      <option value="" disabled>Select platform</option>
                      <option v-for="platform_option in platform_options" :key="platform_option.id" :value="platform_option.id">
                        {{ platform_option.label }}
                      </option>
                    </select>
                  </div>
                  <div class="mb-3 text-start">
                    <label class="ms-2">Principal</label>
                    <input v-model="access_form.principal_name" type="text" class="form-control" placeholder="e.g. Organization Admin" required>
                  </div>
                  <div class="mb-3 text-start">
                    <label class="ms-2">Access Level</label>
                    <select v-model="access_form.access_level" class="form-control">
                      <option>Launch + Configure</option>
                      <option>Readiness Only</option>
                      <option>Launch Test</option>
                      <option>Read Only</option>
                    </select>
                  </div>
                  <div class="mb-3 text-start">
                    <label class="ms-2">Environment</label>
                    <select v-model="access_form.environment" class="form-control">
                      <option>Development</option>
                      <option>Staging</option>
                      <option>UAT</option>
                      <option>Production</option>
                    </select>
                  </div>
                  <div class="modal-footer px-0 pb-0">
                    <button type="submit" class="btn upload-button text-white">Assign Access</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div class="modal fade" id="viewDeploymentChecklistModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <div>
                  <h5 class="modal-title gradient-text mb-1">DevOps Handover</h5>
                  <p class="text-muted small mb-0">
                    {{ deployment_checklist_modal_config?.platform_name || selected_configuration?.platform_name || 'No configuration selected' }}
                  </p>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <div v-if="deployment_handover_loading" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status"></div>
                  <p class="text-muted mt-2 mb-0">Loading DevOps handover...</p>
                </div>
                <div v-else-if="deployment_handover">
                  <div class="row g-3 mb-3 text-start">
                    <div class="col-md-3">
                      <div class="small text-muted">Platform</div>
                      <div class="fw-semibold">{{ handover_platform_name }}</div>
                    </div>
                    <div class="col-md-3">
                      <div class="small text-muted">Environment</div>
                      <div class="fw-semibold">{{ handover_environment }}</div>
                    </div>
                    <div class="col-md-3">
                      <div class="small text-muted">App Code</div>
                      <div class="fw-semibold">{{ handover_app_code }}</div>
                    </div>
                    <div class="col-md-3">
                      <div class="small text-muted">Status</div>
                      <div class="fw-semibold">{{ handover_status }}</div>
                    </div>
                  </div>

                  <div class="alert alert-warning small text-start">
                    Add these values to the one client/environment .env file. Deploy using the shared YAML/docker-compose file. Run readiness checks after deployment.
                  </div>

                  <div class="row g-3">
                    <div class="col-md-6" v-for="entry in displayed_deployment_env_values" :key="entry.key">
                      <div class="platform-checklist-modal-card">
                        <div class="small text-muted mb-1">{{ entry.key }}</div>
                        <div class="fw-semibold platform-secret-value mb-0">{{ entry.value }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-3 text-start">
                    <div class="fw-semibold mb-2">Required platform routes</div>
                    <div v-if="handover_required_routes.length" class="platform-readiness-list">
                      <div class="platform-readiness-item" v-for="(route, index) in handover_required_routes" :key="route.label || route.name || index">
                        <span>{{ route.label || route.name || 'Route' }}</span>
                        <span class="text-break">{{ route.value || route.url || route }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="row g-3">
                  <div class="col-md-6" v-for="entry in checklist_entries" :key="entry.key">
                    <div class="platform-checklist-modal-card">
                      <div class="small text-muted mb-1">{{ entry.label }}</div>
                      <div class="fw-semibold platform-secret-value mb-3">{{ get_display_secret(entry.value) }}</div>
                      <button class="btn btn-sm btn-outline-dark" @click="copy_deployment_value(entry)">Copy value</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-outline-danger" :disabled="!deployment_handover" @click="reveal_deployment_values()">Reveal Deployment Values</button>
                <button class="btn upload-button text-white" @click="copy_deployment_checklist()">Copy Env Values</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
